import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, ArrowLeft, MapPin, Bed, Bath, Maximize, Heart, TrendingDown, Star, MessageSquare, Send, Eye, CalendarDays, Share2, GitCompare } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { useCompare } from '@/hooks/useCompare';
import ApplicationDialog from '@/components/property/ApplicationDialog';
import ReviewsSection from '@/components/property/ReviewsSection';
import ImageGallery from '@/components/property/ImageGallery';

const EMPTY_APP = {
  full_legal_name: '', date_of_birth: '', current_address: '',
  employer: '', job_title: '', monthly_income: '', employment_since: '',
  current_landlord_name: '', current_landlord_phone: '', reason_for_moving: '',
  reference_name: '', reference_relationship: '', reference_phone: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  consent_background_check: false,
};

const REQUIRED_APP_FIELDS = [
  'full_legal_name', 'date_of_birth', 'current_address',
  'employer', 'monthly_income', 'reference_name', 'reference_phone',
  'emergency_contact_name', 'emergency_contact_phone',
];

function computeTotalDiscount(property) {
  if (!property) return 0;
  const base = property.discount_rate || 0;
  const maxDuration = Math.max(
    property.duration_discounts?.['3months'] || 0,
    property.duration_discounts?.['6months'] || 0,
    property.duration_discounts?.['12months'] || 0,
  );
  return base + maxDuration + (property.seasonal_discount || 0);
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const compare = useCompare();

  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showLeaseDialog, setShowLeaseDialog] = useState(false);
  const [leaseData, setLeaseData] = useState({ lease_months: '12', start_date: '', tenant_signature: '', down_payment: '0' });
  const [appData, setAppData] = useState(EMPTY_APP);

  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageContent, setMessageContent] = useState('');

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Property not found');
      navigate(-1);
    }
  }, [id, navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      log.error('Failed to fetch reviews');
    }
  }, [id]);

  const fetchAvailability = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}/availability`);
      setAvailability(response.data.unavailable_ranges || []);
    } catch (error) {
      log.error('Failed to fetch availability');
    }
  }, [id]);

  const checkSaved = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/saved-properties/check/${id}`);
      setIsSaved(response.data.is_saved);
    } catch (error) { log.error('PropertyDetail: check saved failed', error); }
  }, [id]);

  useEffect(() => {
    fetchProperty();
    fetchReviews();
    fetchAvailability();
    if (user?.role === 'tenant') checkSaved();
  }, [user, fetchProperty, fetchReviews, fetchAvailability, checkSaved]);

  const isDateUnavailable = useCallback((date) => {
    const d = date.toISOString().split('T')[0];
    return availability.some(range => d >= range.start_date && d <= range.end_date);
  }, [availability]);

  const totalDiscount = useMemo(() => computeTotalDiscount(property), [property]);
  const discountedPrice = useMemo(() => (
    property ? property.price * (1 - totalDiscount / 100) : 0
  ), [property, totalDiscount]);

  const avgRating = useMemo(() => (
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : null
  ), [reviews]);

  const handleSaveProperty = useCallback(async () => {
    if (!user || user.role !== 'tenant') {
      toast.error('Please login as a tenant to save properties');
      return;
    }
    try {
      if (isSaved) {
        await axios.delete(`${API}/saved-properties/${id}`);
        toast.success('Property removed from saved');
        setIsSaved(false);
      } else {
        await axios.post(`${API}/saved-properties/${id}`, {});
        toast.success('Property saved successfully');
        setIsSaved(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  }, [id, isSaved, user]);

  const handleSubmitReview = useCallback(async ({ rating, comment }) => {
    if (!comment.trim()) {
      toast.error('Please write a comment');
      return false;
    }
    try {
      await axios.post(`${API}/reviews`, {
        property_id: id,
        rating: parseInt(rating, 10),
        comment,
      });
      toast.success('Review submitted!');
      fetchReviews();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
      return false;
    }
  }, [id, fetchReviews]);

  const handleSubmitLease = useCallback(async () => {
    if (!leaseData.tenant_signature.trim() || !leaseData.start_date) {
      toast.error('Please fill move-in date and signature');
      return;
    }
    for (const key of REQUIRED_APP_FIELDS) {
      if (!String(appData[key] || '').trim()) {
        toast.error('Please complete all required application fields');
        return;
      }
    }
    if (!appData.consent_background_check) {
      toast.error('Please consent to a background/credit check to submit your application');
      return;
    }
    if (isDateUnavailable(new Date(leaseData.start_date))) {
      toast.error('Selected move-in date is unavailable. Please choose another date.');
      return;
    }
    try {
      const response = await axios.post(`${API}/leases`, {
        property_id: id,
        lease_months: parseInt(leaseData.lease_months, 10),
        start_date: leaseData.start_date,
        tenant_signature: leaseData.tenant_signature,
        down_payment: parseFloat(leaseData.down_payment) || 0,
        application_data: { ...appData, monthly_income: parseFloat(appData.monthly_income) || 0 },
      });
      toast.success('Application submitted! Awaiting landlord review.');
      setShowLeaseDialog(false);
      navigate(`/leases/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  }, [id, leaseData, appData, isDateUnavailable, navigate]);

  const handleSendMessage = useCallback(async () => {
    if (!messageContent.trim()) {
      toast.error('Please write a message');
      return;
    }
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: property.landlord_id,
        property_id: id,
        content: messageContent,
      });
      toast.success('Message sent!');
      setShowMessageDialog(false);
      setMessageContent('');
      navigate('/messages');
    } catch (error) {
      toast.error('Failed to send message');
    }
  }, [id, property, messageContent, navigate]);

  const handleShare = useCallback(async () => {
    const shareUrl = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/og/property/${property.id}`;
    const shareData = { title: property.title, text: `${property.title} on PrimePocket`, url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch (e) { log.debug('Web Share cancelled or unavailable', e); }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard');
    } catch (e) {
      toast.error('Copy failed — link: ' + shareUrl);
    }
  }, [property]);

  const handleToggleCompare = useCallback(() => {
    if (compare.has(property.id)) {
      compare.remove(property.id);
      toast.success('Removed from Compare');
      return;
    }
    const res = compare.add(property.id);
    if (res.full) {
      toast.error(`You can compare up to ${compare.max} properties. Remove one first.`);
      return;
    }
    toast.success('Added to Compare');
  }, [property, compare]);

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  if (!property) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <Home className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" />
              <span className="text-base sm:text-xl font-bold tracking-tight text-primary truncate">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          <ImageGallery images={property.images} title={property.title} totalDiscount={totalDiscount} />

          <div>
            <div className="flex items-start justify-between mb-5 sm:mb-6 gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight mb-2 sm:mb-3">{property.title}</h1>
                <div className="flex items-center text-sm sm:text-lg text-muted-foreground">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0" /><span className="truncate">{property.location}, {property.city}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs sm:text-sm text-muted-foreground">
                  {avgRating && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`w-4 h-4 sm:w-5 sm:h-5 ${i <= Math.round(avgRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <span className="font-medium">{avgRating}</span>
                      <span>({reviews.length} reviews)</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1" data-testid="property-views-count">
                    <Eye className="w-4 h-4" />
                    <span>{property.views_count || 0} views</span>
                  </div>
                  <div className="flex items-center gap-1" data-testid="property-likes-count">
                    <Heart className="w-4 h-4" />
                    <span>{property.likes_count || 0} likes</span>
                  </div>
                </div>
              </div>
              {user?.role === 'tenant' && (
                <Button data-testid="save-property-detail-btn" onClick={handleSaveProperty} variant="outline" size="lg" className="rounded-full">
                  <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
                </Button>
              )}
              <Button data-testid="share-property-btn" onClick={handleShare} variant="outline" size="lg" className="rounded-full ml-2">
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                data-testid="compare-toggle-btn"
                onClick={handleToggleCompare}
                variant={compare.has(property.id) ? 'default' : 'outline'}
                size="lg"
                className="rounded-full ml-2"
                aria-label="Add to compare"
              >
                <GitCompare className="w-5 h-5" />
                {compare.has(property.id) && <span className="ml-2 text-xs">In Compare</span>}
              </Button>
            </div>

            <Card className="p-8 mb-6 border border-border shadow-sm">
              <div className="flex items-end gap-4 mb-4">
                <div className="text-5xl font-bold text-primary">${discountedPrice.toFixed(0)}</div>
                <div className="text-lg text-muted-foreground mb-2">/month</div>
              </div>
              {totalDiscount > 0 && (
                <div className="flex items-center gap-3">
                  <div className="text-xl text-muted-foreground line-through">${property.price}</div>
                  <div className="flex items-center text-accent font-medium">
                    <TrendingDown className="w-5 h-5 mr-1" />
                    Save ${(property.price - discountedPrice).toFixed(0)}/month
                  </div>
                </div>
              )}
            </Card>

            {user?.role === 'tenant' && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <ApplicationDialog
                  open={showLeaseDialog}
                  onOpenChange={setShowLeaseDialog}
                  propertyTitle={property.title}
                  appData={appData}
                  setAppData={setAppData}
                  leaseData={leaseData}
                  setLeaseData={setLeaseData}
                  isDateUnavailable={isDateUnavailable}
                  onSubmit={handleSubmitLease}
                />

                <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="contact-landlord-btn" size="lg" variant="outline" className="rounded-full">
                      <MessageSquare className="w-4 h-4 mr-2" />Message Landlord
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Contact {property.landlord_name}</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Textarea data-testid="message-textarea" placeholder="Hi, I'm interested in this property..."
                        value={messageContent} onChange={(e) => setMessageContent(e.target.value)} rows={5} />
                      <Button data-testid="message-send-btn" onClick={handleSendMessage} className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                        <Send className="w-4 h-4 mr-2" />Send Message
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="p-6 text-center border border-border">
                <Bed className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">{property.bedrooms}</div>
                <div className="text-sm text-muted-foreground">Bedrooms</div>
              </Card>
              <Card className="p-6 text-center border border-border">
                <Bath className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">{property.bathrooms}</div>
                <div className="text-sm text-muted-foreground">Bathrooms</div>
              </Card>
              <Card className="p-6 text-center border border-border">
                <Maximize className="w-8 h-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">{property.area}</div>
                <div className="text-sm text-muted-foreground">sqft</div>
              </Card>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-medium mb-4">About This Property</h2>
              <p className="text-muted-foreground leading-relaxed">{property.description}</p>
            </div>

            {property.amenities && property.amenities.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-medium mb-4">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((amenity) => (
                    <span key={amenity} className="px-4 py-2 bg-muted text-foreground rounded-full text-sm">{amenity}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-2xl font-medium mb-4 flex items-center gap-2">
                <CalendarDays className="w-6 h-6 text-primary" />Availability
              </h2>
              <Card className="p-6 border border-border">
                <Calendar mode="single" disabled={isDateUnavailable} data-testid="availability-calendar" className="rounded-md" />
                <div className="mt-4 flex items-center gap-6 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-primary/20 border border-primary" />
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm bg-muted line-through" />
                    <span className="line-through">Unavailable / Booked</span>
                  </div>
                </div>
              </Card>
            </div>

            {(property.duration_discounts?.['3months'] > 0 || property.duration_discounts?.['6months'] > 0 || property.duration_discounts?.['12months'] > 0) && (
              <Card className="p-6 mb-8 bg-accent/5 border-accent/20">
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <TrendingDown className="w-5 h-5 mr-2 text-accent" />Additional Duration Discounts
                </h3>
                <div className="space-y-2">
                  {property.duration_discounts['3months'] > 0 && <div className="flex justify-between"><span>3 Months Lease:</span><span className="font-medium text-accent">-{property.duration_discounts['3months']}%</span></div>}
                  {property.duration_discounts['6months'] > 0 && <div className="flex justify-between"><span>6 Months Lease:</span><span className="font-medium text-accent">-{property.duration_discounts['6months']}%</span></div>}
                  {property.duration_discounts['12months'] > 0 && <div className="flex justify-between"><span>12 Months Lease:</span><span className="font-medium text-accent">-{property.duration_discounts['12months']}%</span></div>}
                </div>
              </Card>
            )}
          </div>
        </div>

        <ReviewsSection
          reviews={reviews}
          canReview={user?.role === 'tenant'}
          onSubmitReview={handleSubmitReview}
        />
      </div>
    </div>
  );
}
